import { useCallback, useEffect, useRef } from "react";
import { IonIcon } from "@/components/icons/AppIcon";

const navBtn =
  "flex h-11 w-11 items-center justify-center rounded-xl border border-[#2C2E29] bg-[#1C1D1A] text-[#EAEBE7] transition-colors hover:border-[#CAA362]/50 hover:text-[#E0BE82] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#CAA362] disabled:opacity-30";

export default function Lightbox({ screens, index, onChange, onClose }) {
  const closeRef = useRef(null);
  const screen = screens[index];
  const prev = useCallback(() => index > 0 && onChange(index - 1), [index, onChange]);
  const next = useCallback(() => index < screens.length - 1 && onChange(index + 1), [index, onChange, screens.length]);

  // Browser back button closes the viewer
  useEffect(() => {
    if (!window.history.state?.waidLightbox) window.history.pushState({ waidLightbox: true }, "");
    const onPop = () => onClose(false);
    window.addEventListener("popstate", onPop);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("popstate", onPop);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose(true);
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${screen.group}: ${screen.caption}`}
      data-testid="lightbox"
      className="fixed inset-0 z-[100] flex flex-col bg-[#0B0C0A]/95 backdrop-blur-md"
      onClick={(e) => e.target === e.currentTarget && onClose(true)}
    >
      <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-8">
        <div className="min-w-0 text-sm">
          <p data-testid="lightbox-caption" className="truncate font-semibold text-[#EAEBE7]">
            {screen.caption}
            {screen.testVersion && <span className="ml-2 text-xs font-semibold uppercase tracking-wider text-[#CAA362]">Testversie</span>}
          </p>
          <p className="truncate text-xs text-[#8E9189]">
            {screen.group} · {index + 1} / {screens.length}
          </p>
        </div>
        <button ref={closeRef} data-testid="lightbox-close" onClick={() => onClose(true)} aria-label="Sluiten" className={navBtn}>
          <IonIcon name="close" size={22} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center gap-3 px-3 pb-4 md:gap-6 md:px-8" onClick={(e) => e.target === e.currentTarget && onClose(true)}>
        <button data-testid="lightbox-prev" onClick={prev} disabled={index === 0} aria-label="Vorige" className={`${navBtn} hidden sm:flex`}>
          <IonIcon name="chevron-back" size={20} />
        </button>
        <img
          key={screen.full}
          data-testid="lightbox-image"
          src={screen.full}
          alt={screen.alt}
          className="h-auto max-h-full w-auto max-w-full rounded-2xl object-contain"
        />
        <button data-testid="lightbox-next" onClick={next} disabled={index === screens.length - 1} aria-label="Volgende" className={`${navBtn} hidden sm:flex`}>
          <IonIcon name="chevron-forward" size={20} />
        </button>
      </div>

      <div className="flex justify-center gap-3 pb-5 sm:hidden">
        <button data-testid="lightbox-prev-mobile" onClick={prev} disabled={index === 0} aria-label="Vorige" className={navBtn}>
          <IonIcon name="chevron-back" size={20} />
        </button>
        <button data-testid="lightbox-next-mobile" onClick={next} disabled={index === screens.length - 1} aria-label="Volgende" className={navBtn}>
          <IonIcon name="chevron-forward" size={20} />
        </button>
      </div>
    </div>
  );
}
